import { Search, Bell, Crown, TrendingUp, Gift, Flame, ArrowRight, MapPin, ChevronRight, Menu, ShoppingBag, Wrench, Sparkles, Zap, Tag, UtensilsCrossed, Star, BadgeCheck, Truck, Shield, Clock, Car, Building2, Smartphone, Monitor, Shirt, WashingMachine } from 'lucide-react';
import AdSenseBanner from '@/components/AdSenseBanner';
import { useNavigate } from 'react-router-dom';
import { useCategories, useStores, useProducts, toCartProduct } from '@/hooks/useData';
import { useSettings } from '@/contexts/SettingsContext';
import ProductCard from '@/components/ProductCard';
import StoreCard from '@/components/StoreCard';
import AnimatedSearchBar from '@/components/AnimatedSearchBar';
import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { useState, useEffect, forwardRef, useCallback, useRef } from 'react';
import { useNotificationCount } from '@/hooks/useNotifications';
import logoMark from '@/assets/logo-fennec-cart-mark.webp';
import catStores from '@/assets/icons/cat-stores.webp';
import catRealEstate from '@/assets/icons/cat-realestate.webp';
import catCars from '@/assets/icons/cat-cars.webp';
import catRestaurants from '@/assets/icons/cat-restaurants.webp';
import catServices from '@/assets/icons/cat-services.webp';
import catTaxi from '@/assets/icons/cat-taxi.webp';
import catPhones from '@/assets/icons/cat-phones.webp';
import catFashion from '@/assets/icons/cat-fashion.webp';
import catParts from '@/assets/icons/cat-parts.webp';
import catComputers from '@/assets/icons/cat-computers.webp';
import catAppliances from '@/assets/icons/cat-appliances.webp';
import catBeauty from '@/assets/icons/cat-beauty.webp';
import catFurniture from '@/assets/icons/cat-furniture.webp';
import catSports from '@/assets/icons/cat-sports.webp';
import catMaterials from '@/assets/icons/cat-materials.webp';
import catFood from '@/assets/icons/cat-food.webp';
import catLeisure from '@/assets/icons/cat-leisure.webp';
import catTravel from '@/assets/icons/cat-travel.webp';
import catJobs from '@/assets/icons/cat-jobs.webp';
import catCrafts from '@/assets/icons/cat-crafts.webp';
import catHotels from '@/assets/icons/cat-hotels.webp';
import catMedical from '@/assets/icons/cat-medical.webp';
import SEOHead from '@/components/SEOHead';
import { organizationSchema, websiteSchema } from '@/lib/seoSchemas';

const HomePage = () => {
  const navigate = useNavigate();
  const { t, formatAmount, language } = useSettings();
  const { data: categories = [] } = useCategories();
  const { data: stores = [] } = useStores();
  const { data: allProducts = [], isLoading } = useProducts();
  const [bannerIndex, setBannerIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'marketplace'>('marketplace');
  const notifCount = useNotificationCount();

  const [catPage, setCatPage] = useState(0);
  const catTouchStartX = useRef<number | null>(null);

  const ui = language === 'ar'
    ? {
        deliverTo: 'التوصيل إلى الجزائر',
        allMenu: 'الكل',
        fastDelivery: 'توصيل سريع',
        allStates: 'لكل الولايات',
        securePay: 'دفع آمن',
        securePaySub: '100% محمي',
        originalProducts: 'منتجات أصلية',
        originalProductsSub: 'ضمان الجودة',
        freeReturn: 'استرجاع مجاني',
        freeReturnSub: '7 أيام',
        taxiTitle: '🚖 خدمة التاكسي',
        taxiSubtitle: 'اطلب رحلتك الآن • سائقين موثوقين • أسعار تنافسية',
        quickFlash: 'تخفيضات سريعة',
        quickCoupons: 'كوبونات',
        quickNew: 'جديد',
        quickTop: 'الأعلى تقييماً',
        emptyTitle: 'مرحباً بك في Marché DZ',
        emptyDesc: 'المنصة قيد التحضير. ستكون المنتجات متاحة قريباً.',
        categories: {
          stores: 'المتاجر',
          realEstate: 'عقارات',
          cars: 'سيارات و مركبات',
          parts: 'قطع غيار',
          phones: 'هواتف و إكسسوارات',
          computers: 'معلوماتية',
          appliances: 'أجهزة منزلية',
          fashion: 'أزياء و موضة',
          beauty: 'صحة و جمال',
          furniture: 'أثاث و منزل',
          leisure: 'ترفيه و ألعاب',
          sports: 'رياضة',
          jobs: 'توظيف',
          materials: 'مواد و معدات',
          food: 'مواد غذائية',
          travel: 'سفر و سياحة',
          restaurants: 'مطاعم',
          services: 'خدمات',
          taxi: 'تاكسي',
          hotels: 'فنادق و منتجعات',
          crafts: 'حرف يدوية',
          medical: 'أطباء و تمريض',
        },
        footer: {
          about: 'من نحن',
          privacy: 'سياسة الخصوصية',
          terms: 'شروط الاستخدام',
          contact: 'اتصل بنا',
          adPolicy: 'سياسة الإعلانات',
        },
      }
    : language === 'fr'
      ? {
          deliverTo: 'Livraison en Algérie',
          allMenu: 'Tout',
          fastDelivery: 'Livraison rapide',
          allStates: 'Toutes les wilayas',
          securePay: 'Paiement sécurisé',
          securePaySub: '100% protégé',
          originalProducts: 'Produits authentiques',
          originalProductsSub: 'Qualité garantie',
          freeReturn: 'Retour gratuit',
          freeReturnSub: '7 jours',
          taxiTitle: '🚖 Service Taxi',
          taxiSubtitle: 'Réservez maintenant • Chauffeurs fiables • Prix compétitifs',
          quickFlash: 'Flash Sale',
          quickCoupons: 'Coupons',
          quickNew: 'Nouveautés',
          quickTop: 'Top notés',
          emptyTitle: 'Bienvenue sur Marché DZ',
          emptyDesc: 'La plateforme est en préparation. Les produits arrivent bientôt.',
          categories: {
            stores: 'Boutiques',
            realEstate: 'Immobilier',
            cars: 'Voitures & Véhicules',
            parts: 'Pièces auto',
            phones: 'Téléphones & Accessoires',
            computers: 'Informatique',
            appliances: 'Électroménager',
            fashion: 'Mode',
            beauty: 'Santé & Beauté',
            furniture: 'Maison & Meubles',
            leisure: 'Loisirs & Jeux',
            sports: 'Sport',
            jobs: 'Emploi',
            materials: 'Matériaux & Équipements',
            food: 'Produits alimentaires',
            travel: 'Voyage & Tourisme',
            restaurants: 'Restaurants',
            services: 'Services',
            taxi: 'Taxi',
            hotels: 'Hôtels & Resorts',
            crafts: 'Artisanat',
            medical: 'Médecins & Soins',
          },
          footer: {
            about: 'À propos',
            privacy: 'Confidentialité',
            terms: 'Conditions',
            contact: 'Contact',
            adPolicy: 'Politique des annonces',
          },
        }
      : {
          deliverTo: 'Deliver across Algeria',
          allMenu: 'All',
          fastDelivery: 'Fast delivery',
          allStates: 'All states',
          securePay: 'Secure payment',
          securePaySub: '100% protected',
          originalProducts: 'Original products',
          originalProductsSub: 'Quality guaranteed',
          freeReturn: 'Free return',
          freeReturnSub: '7 days',
          taxiTitle: '🚖 Taxi Service',
          taxiSubtitle: 'Book now • Trusted drivers • Competitive prices',
          quickFlash: 'Flash Sale',
          quickCoupons: 'Coupons',
          quickNew: 'New Arrivals',
          quickTop: 'Top Rated',
          emptyTitle: 'Welcome to Marché DZ',
          emptyDesc: 'The marketplace is being prepared. Products will be available soon.',
          categories: {
            stores: 'Stores',
            realEstate: 'Real Estate',
            cars: 'Cars & Vehicles',
            parts: 'Spare Parts',
            phones: 'Phones & Accessories',
            computers: 'Computers',
            appliances: 'Home Appliances',
            fashion: 'Fashion',
            beauty: 'Health & Beauty',
            furniture: 'Home & Furniture',
            leisure: 'Leisure & Games',
            sports: 'Sports',
            jobs: 'Jobs',
            materials: 'Materials & Equipment',
            food: 'Food Products',
            travel: 'Travel & Tourism',
            restaurants: 'Restaurants',
            services: 'Services',
            taxi: 'Taxi',
            hotels: 'Hotels & Resorts',
            crafts: 'Handicrafts',
            medical: 'Doctors & Nursing',
          },
          footer: {
            about: 'About',
            privacy: 'Privacy Policy',
            terms: 'Terms',
            contact: 'Contact',
            adPolicy: 'Ad Policy',
          },
        };

  const openServiceCategory = useCallback((categoryName?: string) => {
    if (categoryName) {
      navigate(`/services?category=${encodeURIComponent(categoryName)}`);
    } else {
      navigate('/services');
    }
  }, [navigate]);

  const translatedDeliverTo = t('home.deliver_to');
  const deliverToText = translatedDeliverTo === 'home.deliver_to' ? ui.deliverTo : translatedDeliverTo;

  const saleProducts = allProducts.filter(p => p.badge === 'sale');
  const newProducts = allProducts.filter(p => p.badge === 'new');
  const hotProducts = allProducts.filter(p => p.badge === 'hot');

  const banners = [
    {
      title: t('home.discount_up_to'),
      subtitle: t('home.on_products'),
      cta: t('home.shop_now'),
      gradient: 'from-[hsl(220,90%,18%)] via-[hsl(220,85%,28%)] to-[hsl(215,80%,40%)]',
      icon: '🎁',
      accentColor: 'hsl(var(--prime-blue-light))',
    },
    {
      title: t('home.new_arrivals').replace(/[✨🔥]/g, '').trim(),
      subtitle: t('home.exclusive'),
      cta: t('home.shop_now'),
      gradient: 'from-[hsl(24,90%,20%)] via-[hsl(24,85%,35%)] to-[hsl(32,90%,45%)]',
      icon: '👑',
      accentColor: 'hsl(var(--primary-glow))',
    },
    {
      title: t('home.best_sellers').replace(/[✨🔥]/g, '').trim(),
      subtitle: t('home.exclusive'),
      cta: t('home.shop_now'),
      gradient: 'from-[hsl(152,60%,12%)] via-[hsl(152,55%,25%)] to-[hsl(160,50%,38%)]',
      icon: '🔥',
      accentColor: 'hsl(var(--success))',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => setBannerIndex(i => (i + 1) % banners.length), 4000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const productsByCategory = categories.slice(0, 8).map(cat => ({
    category: cat,
    products: allProducts.filter(p => p.category_id === cat.id).slice(0, 4),
  })).filter(g => g.products.length > 0);

  return (
    <div className="pb-24 min-h-screen bg-background">
      <SEOHead
        url="/"
        title="سوق إلكتروني في الجزائر - تسوق أونلاين"
        description="Marché DZ - أكبر سوق إلكتروني في الجزائر. تسوق أونلاين: ملابس، إلكترونيات، هواتف، أحذية، مطاعم، خدمات مع توصيل سريع لجميع الولايات."
        keywords="سوق إلكتروني الجزائر, تسوق أونلاين, marketplace algérie, boutique en ligne, marché dz, ملابس, إلكترونيات, هواتف, مطاعم الجزائر, خدمات"
        jsonLd={[organizationSchema, websiteSchema]}
      />
      {/* ═══ PREMIUM HEADER ═══ */}
      <header className="sticky top-0 z-40 safe-top">
        <div className="relative overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(220,35%,12%)] to-[hsl(220,30%,16%)]" />
          {/* Subtle mesh */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, hsl(var(--primary)) 0%, transparent 50%), radial-gradient(circle at 80% 50%, hsl(var(--prime-blue)) 0%, transparent 50%)',
          }} />
          
          <div className="relative flex items-center gap-2 px-3 pt-3 pb-2">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-14 h-14 rounded-xl bg-background flex items-center justify-center shadow-glow-primary p-1">
                <img src={logoMark} alt="Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] font-black text-primary-foreground tracking-tight leading-none">Marché</span>
                <span className="text-[11px] font-bold text-primary/80 tracking-widest uppercase leading-none mt-0.5">DZ</span>
              </div>
            </div>
            
            <AnimatedSearchBar />
            
            <button aria-label="Notifications" onClick={() => navigate('/notifications')} className="relative w-10 h-10 flex items-center justify-center text-primary-foreground/80 hover:text-primary-foreground rounded-xl hover:bg-primary-foreground/5 transition-all">
              <Bell size={20} />
              {notifCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-destructive text-destructive-foreground text-[9px] font-black rounded-full px-1 border-2 border-[hsl(220,35%,12%)] pulse-ring"
                >
                  {notifCount > 9 ? '9+' : notifCount}
                </motion.span>
              )}
            </button>
          </div>

          {/* Location + Trust bar */}
          <div className="relative flex items-center justify-between px-3 pb-2.5">
            <div className="flex items-center gap-1.5 text-primary-foreground/50">
              <MapPin size={11} />
              <span className="text-[10px] font-medium">{deliverToText}</span>
              <ChevronRight size={10} />
            </div>
            <div className="flex items-center gap-3">
              {[
                { icon: <Truck size={9} />, label: ui.fastDelivery },
                { icon: <Shield size={9} />, label: ui.securePay },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-1 text-primary-foreground/30">
                  {item.icon}
                  <span className="text-[8px] font-semibold">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category strip */}
        <div className="bg-gradient-to-r from-[hsl(220,30%,14%)] to-[hsl(220,25%,18%)] overflow-x-auto scrollbar-none border-b border-primary-foreground/5">
          <div className="flex items-center">
            <button className="flex items-center gap-1.5 px-3.5 py-2.5 text-primary-foreground/90 text-[11px] font-bold flex-shrink-0 border-r border-primary-foreground/8 hover:bg-primary-foreground/5 transition-colors">
              <Menu size={13} />
              <span>{ui.allMenu}</span>
            </button>
            {categories.slice(0, 6).map(cat => (
              <button
                key={cat.id}
                onClick={() => navigate(`/search?cat=${cat.id}`)}
                className="px-3.5 py-2.5 text-primary-foreground/50 text-[11px] font-medium flex-shrink-0 hover:text-primary-foreground/90 hover:bg-primary-foreground/5 transition-all whitespace-nowrap"
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ═══ CATEGORY GRID - Swipeable Pages ═══ */}
      {(() => {
        const allCats = [
          { img: catCars, label: ui.categories.cars, action: () => navigate('/cars'), featured: true },
          { img: catStores, label: ui.categories.stores, action: () => navigate('/search') },
          { img: catRealEstate, label: ui.categories.realEstate, action: () => navigate('/real-estate') },
          { img: catParts, label: ui.categories.parts, action: () => openServiceCategory('Mécanique Auto') },
          { img: catPhones, label: ui.categories.phones, action: () => openServiceCategory('Informatique') },
          { img: catComputers, label: ui.categories.computers, action: () => openServiceCategory('Informatique') },
          { img: catAppliances, label: ui.categories.appliances, action: () => openServiceCategory('Climatisation') },
          { img: catFashion, label: ui.categories.fashion, action: () => openServiceCategory('Couture') },
          { img: catBeauty, label: ui.categories.beauty, action: () => openServiceCategory('Médecins & Soins') },
          { img: catFurniture, label: ui.categories.furniture, action: () => openServiceCategory('Menuiserie') },
          { img: catLeisure, label: ui.categories.leisure, action: () => openServiceCategory('Photographie') },
          { img: catSports, label: ui.categories.sports, action: () => navigate('/services') },
          { img: catJobs, label: ui.categories.jobs, action: () => navigate('/jobs') },
          { img: catMaterials, label: ui.categories.materials, action: () => openServiceCategory('Matériaux & Équipements') },
          { img: catFood, label: ui.categories.food, action: () => openServiceCategory('Alimentation') },
          { img: catTravel, label: ui.categories.travel, action: () => openServiceCategory('Voyage & Tourisme') },
          { img: catRestaurants, label: ui.categories.restaurants, action: () => navigate('/restaurants-list') },
          { img: catServices, label: ui.categories.services, action: () => navigate('/services') },
          { img: catTaxi, label: ui.categories.taxi, action: () => navigate('/taxi') },
          { img: catHotels, label: ui.categories.hotels, action: () => openServiceCategory('Hôtels & Resorts') },
          { img: catCrafts, label: ui.categories.crafts, action: () => openServiceCategory('Artisanat') },
          { img: catMedical, label: ui.categories.medical, action: () => openServiceCategory('Médecins & Soins') },
        ];
        const perPage = 8;
        const totalPages = Math.ceil(allCats.length / perPage);
        const pageCats = allCats.slice(catPage * perPage, (catPage + 1) * perPage);

        return (
          <div className="mt-5 mb-2">
            <div
              className="px-4 touch-pan-y"
              onTouchStart={(e) => {
                const t = e.touches[0];
                catTouchStartX.current = t.clientX;
              }}
              onTouchEnd={(e) => {
                if (catTouchStartX.current === null) return;
                const t = e.changedTouches[0];
                const dx = t.clientX - catTouchStartX.current;
                catTouchStartX.current = null;
                if (Math.abs(dx) < 50) return;
                if (dx < -50) {
                  setCatPage(p => Math.min(p + 1, totalPages - 1));
                } else if (dx > 50) {
                  setCatPage(p => Math.max(p - 1, 0));
                }
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={catPage}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                  className="grid grid-cols-4 gap-x-3 gap-y-4"
                >
                  {pageCats.map((cat: any, i: number) => (
                    <motion.button
                      key={cat.label}
                      data-cat-button="true"
                      whileTap={{ scale: 0.92 }}
                      onClick={cat.action}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div className={`w-[72px] h-[72px] rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 relative overflow-hidden ${
                        cat.featured
                          ? 'bg-gradient-to-br from-[hsl(25,100%,20%)] to-[hsl(40,100%,30%)] border-2 border-[hsl(40,100%,55%)]'
                          : 'bg-[hsl(0,0%,18%)] border border-[hsl(0,0%,25%)]'
                      }`}>
                        {cat.featured && (
                          <>
                            <motion.div
                              animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="absolute inset-0 rounded-full border-2 border-[hsl(40,100%,55%)]"
                            />
                            <div className="absolute inset-0 rounded-full opacity-30" style={{
                              background: 'radial-gradient(circle, hsl(40,100%,55%) 0%, transparent 70%)',
                            }} />
                          </>
                        )}
                        <img src={cat.img} alt={cat.label} className={`object-contain group-hover:scale-110 transition-transform duration-300 ${cat.featured ? 'w-[58px] h-[58px]' : 'w-[54px] h-[54px]'}`} />
                      </div>
                      <span className={`text-[10px] font-bold text-center leading-tight max-w-[80px] ${cat.featured ? 'text-[hsl(30,90%,35%)]' : 'text-foreground'}`}>{cat.label}</span>
                    </motion.button>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
            {/* Dots indicator */}
             <div className="flex items-center justify-center gap-1 mt-3">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCatPage(idx)}
                  aria-label={`Page ${idx + 1}`}
                  className="min-w-[24px] min-h-[24px] flex items-center justify-center"
                >
                  <span className={`block h-2 rounded-full transition-all duration-300 ${
                    catPage === idx
                      ? 'w-6 bg-primary'
                      : 'w-2 bg-muted-foreground/30'
                  }`} />
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ═══ TAXI NEON BANNER - Always Visible ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
        onClick={() => navigate('/taxi')}
        className="relative mx-3 mt-4 mb-3 rounded-2xl overflow-hidden cursor-pointer group"
        style={{
          background: 'linear-gradient(135deg, hsl(220,30%,8%) 0%, hsl(260,30%,12%) 50%, hsl(220,30%,8%) 100%)',
        }}
      >
        {/* Neon glow effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-32 h-32 rounded-full blur-3xl bg-[hsl(180,100%,50%)]" />
          <div className="absolute bottom-0 right-1/4 w-40 h-40 rounded-full blur-3xl bg-[hsl(280,100%,60%)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-24 rounded-full blur-3xl bg-[hsl(45,100%,55%)]" />
        </div>
        {/* Animated neon border */}
        <div className="absolute inset-0 rounded-2xl" style={{
          background: 'linear-gradient(90deg, hsl(180,100%,50%), hsl(280,100%,60%), hsl(45,100%,55%), hsl(180,100%,50%))',
          backgroundSize: '300% 100%',
          animation: 'neon-border-slide 3s linear infinite',
          padding: '1.5px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }} />

        <div className="relative flex items-center gap-3 px-4 py-4">
          {/* Taxi icon with neon ring */}
          <div className="relative flex-shrink-0">
            <motion.div
              animate={{ boxShadow: [
                '0 0 15px hsl(45,100%,55%), 0 0 30px hsl(45,100%,55%,0.3)',
                '0 0 25px hsl(180,100%,50%), 0 0 50px hsl(180,100%,50%,0.3)',
                '0 0 15px hsl(45,100%,55%), 0 0 30px hsl(45,100%,55%,0.3)',
              ]}}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-16 h-16 rounded-full bg-[hsl(0,0%,10%)] flex items-center justify-center border border-[hsl(45,90%,55%,0.5)]"
            >
              <img src={catTaxi} alt="تاكسي" className="w-11 h-11 object-contain" />
            </motion.div>
            {/* Pulse ring */}
            <motion.div
              animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="absolute inset-0 rounded-full border-2 border-[hsl(45,100%,55%)]"
            />
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0">
            <motion.h2
              animate={{
                textShadow: [
                  '0 0 8px hsl(45,100%,55%,0.8), 0 0 16px hsl(45,100%,55%,0.4)',
                  '0 0 12px hsl(180,100%,50%,0.8), 0 0 24px hsl(180,100%,50%,0.4)',
                  '0 0 8px hsl(45,100%,55%,0.8), 0 0 16px hsl(45,100%,55%,0.4)',
                ],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              className="text-lg font-black text-primary-foreground leading-tight"
            >
              {ui.taxiTitle}
            </motion.h2>
            <p className="text-[11px] text-primary-foreground/50 mt-0.5 font-medium">
              {ui.taxiSubtitle}
            </p>
          </div>

          {/* Arrow with neon effect */}
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
            className="flex-shrink-0"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, hsl(45,100%,55%), hsl(35,100%,50%))',
              boxShadow: '0 0 20px hsl(45,100%,55%,0.4), 0 0 40px hsl(45,100%,55%,0.2)',
            }}>
              <ArrowRight size={18} className="text-[hsl(220,30%,8%)]" />
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* ═══ MARKETPLACE CONTENT ═══ */}
      {/* ═══ HERO BANNER ═══ */}
      <div className="relative overflow-hidden mx-3 mt-3 rounded-3xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={bannerIndex}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={`bg-gradient-to-br ${banners[bannerIndex].gradient} px-6 py-10 min-h-[185px] flex flex-col justify-center relative overflow-hidden rounded-3xl`}
          >
            {/* Luxury decorative elements */}
            <div className="absolute inset-0 noise-overlay opacity-40" />
            <motion.div
              animate={{ y: [-8, 8, -8], rotate: [0, 8, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-6 end-6 text-6xl opacity-20 select-none"
            >
              {banners[bannerIndex].icon}
            </motion.div>
            
            {/* Glow orbs */}
            <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full blur-3xl" style={{ background: `${banners[bannerIndex].accentColor}`, opacity: 0.08 }} />
            <div className="absolute -bottom-12 -start-12 w-40 h-40 rounded-full blur-3xl bg-primary-foreground/3" />

            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-[10px] font-bold text-primary-foreground/40 uppercase tracking-[0.25em] mb-1"
            >
              {banners[bannerIndex].subtitle}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-black text-primary-foreground leading-tight tracking-tight"
            >
              {banners[bannerIndex].title}
            </motion.h2>
            <motion.button
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/search')}
              className="self-start mt-5 bg-primary-foreground text-foreground text-[11px] font-bold px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              {banners[bannerIndex].cta}
              <ArrowRight size={13} />
            </motion.button>
          </motion.div>
        </AnimatePresence>

        {/* Elegant indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setBannerIndex(i)}
              aria-label={`Banner ${i + 1}`}
              className="min-w-[24px] min-h-[24px] flex items-center justify-center"
            >
              <span className={`block rounded-full transition-all duration-500 ${
                i === bannerIndex
                  ? 'bg-primary-foreground w-8 h-2 shadow-md'
                  : 'bg-primary-foreground/25 w-2 h-2 hover:bg-primary-foreground/50'
              }`} />
            </button>
          ))}
        </div>
      </div>

      {/* ═══ TRUST BADGES ═══ */}
      <div className="flex gap-2 px-3 mt-3 overflow-x-auto scrollbar-none">
        {[
          { icon: <Truck size={14} />, title: ui.fastDelivery, sub: ui.allStates, color: 'text-primary' },
          { icon: <Shield size={14} />, title: ui.securePay, sub: ui.securePaySub, color: 'text-success' },
          { icon: <BadgeCheck size={14} />, title: ui.originalProducts, sub: ui.originalProductsSub, color: 'text-[hsl(var(--prime-blue))]' },
          { icon: <Clock size={14} />, title: ui.freeReturn, sub: ui.freeReturnSub, color: 'text-warning' },
        ].map((badge, i) => (
          <motion.div
            key={badge.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-card border border-border/50 flex-shrink-0 min-w-[130px]"
          >
            <div className={`w-8 h-8 rounded-xl bg-muted/50 flex items-center justify-center ${badge.color}`}>
              {badge.icon}
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-foreground leading-tight">{badge.title}</span>
              <span className="text-[8px] text-muted-foreground font-medium">{badge.sub}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ═══ QUICK ACTIONS ═══ */}
      <div className="px-3 mt-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {[
            { icon: <Zap size={13} />, label: ui.quickFlash, gradient: 'from-destructive/15 to-destructive/5', textColor: 'text-destructive', borderColor: 'border-destructive/20' },
            { icon: <Tag size={13} />, label: ui.quickCoupons, gradient: 'from-primary/15 to-primary/5', textColor: 'text-primary', borderColor: 'border-primary/20' },
            { icon: <Sparkles size={13} />, label: ui.quickNew, gradient: 'from-success/15 to-success/5', textColor: 'text-success', borderColor: 'border-success/20' },
            { icon: <Crown size={13} />, label: ui.quickTop, gradient: 'from-warning/15 to-warning/5', textColor: 'text-warning', borderColor: 'border-warning/20' },
          ].map((action, i) => (
            <motion.button
              key={action.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              whileTap={{ scale: 0.93 }}
              whileHover={{ y: -2 }}
              onClick={() => navigate('/search')}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-[11px] font-bold flex-shrink-0 border ${action.borderColor} bg-gradient-to-r ${action.gradient} ${action.textColor} transition-all shadow-sm hover:shadow-md`}
            >
              {action.icon}
              {action.label}
            </motion.button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 p-3 mt-3">
          {[1,2,3,4].map(i => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          {/* ═══ CATEGORY GRID ═══ */}
          <section className="p-3 mt-1">
            <div className="grid grid-cols-2 gap-3">
              {productsByCategory.map((group, gi) => (
                <AmazonCard
                  key={group.category.id}
                  title={group.category.name}
                  icon={group.category.icon}
                  items={group.products.slice(0, 4).map(p => ({
                    id: p.id,
                    image: p.images?.[0] || '/placeholder.svg',
                    label: p.name,
                  }))}
                  onAction={() => navigate(`/search?cat=${group.category.id}`)}
                  actionLabel={t('home.discover') || 'Découvrir'}
                  index={gi}
                />
              ))}

              {saleProducts.length > 0 && (
                <AmazonCard
                  title={t('home.special_offers').replace(/[🔥✨]/g, '').trim()}
                  items={saleProducts.slice(0, 4).map(p => ({
                    id: p.id,
                    image: p.images?.[0] || '/placeholder.svg',
                    label: formatAmount(p.price),
                  }))}
                  onAction={() => navigate('/search')}
                  actionLabel={t('home.shop_now')}
                  index={productsByCategory.length}
                  titleAccent
                />
              )}

              {newProducts.length > 0 && (
                <AmazonCard
                  title={t('home.new_arrivals').replace(/[🔥✨]/g, '').trim()}
                  items={newProducts.slice(0, 4).map(p => ({
                    id: p.id,
                    image: p.images?.[0] || '/placeholder.svg',
                    label: p.name,
                  }))}
                  onAction={() => navigate('/search')}
                  actionLabel={t('home.shop_now')}
                  index={productsByCategory.length + 1}
                />
              )}
            </div>
          </section>

          {/* AdSense between categories and stores — only with sufficient content */}
          {allProducts.length >= 6 && stores.length >= 2 && <AdSenseBanner className="mx-3" contentCount={allProducts.length} minContentItems={6} />}

          {/* ═══ FEATURED STORES ═══ */}
          {stores.length > 0 && (
            <section className="mt-2">
              <SectionHeader title={t('home.featured_stores')} icon={<Crown size={15} className="text-warning" />} />
              <div className="flex gap-3 px-3 overflow-x-auto scrollbar-none pb-2">
                {stores.map((store) => <StoreCard key={store.id} store={store} />)}
              </div>
            </section>
          )}

          {/* ═══ BEST SELLERS ═══ */}
          {hotProducts.length > 0 && (
            <section className="mt-4">
              <SectionHeader
                title={t('home.best_sellers').replace(/[✨🔥]/g, '').trim()}
                icon={<TrendingUp size={15} className="text-destructive" />}
              />
              <div className="flex gap-2.5 px-3 overflow-x-auto scrollbar-none pb-2">
                {hotProducts.map((p, i) => (
                  <div key={p.id} className="flex-shrink-0 w-[160px]">
                    <ProductCard product={toCartProduct(p)} index={i} compact />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* AdSense before all products — only with sufficient content */}
          {allProducts.length >= 8 && <AdSenseBanner className="mx-3" contentCount={allProducts.length} minContentItems={8} />}

          {/* ═══ ALL PRODUCTS ═══ */}
          {allProducts.length > 0 && (
            <section className="mt-4 pb-4">
              <SectionHeader title={t('home.all_products')} icon={<Gift size={15} className="text-primary" />} />
              <div className="grid grid-cols-2 gap-3 px-3">
                {allProducts.map((p, i) => (
                  <>{(i > 0 && i % 8 === 0) && <div className="col-span-2"><AdSenseBanner contentCount={allProducts.length} minContentItems={8} /></div>}<ProductCard key={p.id} product={toCartProduct(p)} index={i} /></>
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {allProducts.length === 0 && categories.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <motion.div
                animate={{ y: [-4, 4, -4] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mb-5 border border-primary/10"
              >
                <Gift size={36} className="text-primary" />
              </motion.div>
              <h3 className="text-lg font-bold text-foreground mb-2">{ui.emptyTitle}</h3>
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                {ui.emptyDesc}
              </p>
            </div>
          )}
        </>
      )}
      {/* Footer Links */}
      <div className="flex items-center justify-center gap-3 py-4 pb-6 flex-wrap">
        <a href="/about" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">{ui.footer.about}</a>
        <span className="text-muted-foreground/50 text-[8px]">•</span>
        <a href="/privacy" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">{ui.footer.privacy}</a>
        <span className="text-muted-foreground/50 text-[8px]">•</span>
        <a href="/terms" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">{ui.footer.terms}</a>
        <span className="text-muted-foreground/50 text-[8px]">•</span>
        <a href="/contact" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">{ui.footer.contact}</a>
        <span className="text-muted-foreground/50 text-[8px]">•</span>
        <a href="/ad-policy" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">{ui.footer.adPolicy}</a>
        <span className="text-muted-foreground/50 text-[8px]">•</span>
        <span className="text-[10px] text-muted-foreground/70">© 2026 Marché DZ</span>
      </div>
    </div>
  );
};

/* ─── Amazon-Style Premium Card ─── */
const AmazonCard = ({
  title, icon, items, onAction, actionLabel, index, titleAccent,
}: {
  title: string;
  icon?: string | null;
  items: { id: string; image: string; label: string }[];
  onAction: () => void;
  actionLabel: string;
  index: number;
  titleAccent?: boolean;
}) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-card rounded-2xl border border-border/50 overflow-hidden flex flex-col premium-shadow hover:premium-shadow-hover transition-all duration-400 group"
    >
      <div className="px-3 pt-3.5 pb-2 flex items-center gap-2">
        {icon && <span className="text-lg">{icon}</span>}
        <h3 className={`text-[13px] font-extrabold leading-tight tracking-tight ${titleAccent ? 'text-destructive' : 'text-foreground'}`}>
          {title}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-1.5 px-2.5 flex-1">
        {items.slice(0, 4).map((item) => (
          <div key={item.id} className="cursor-pointer group/item" onClick={() => navigate(`/product/${item.id}`)}>
            <div className="aspect-square rounded-xl overflow-hidden bg-muted/15 border border-border/20 group-hover/item:border-primary/20 transition-all">
              <img src={item.image} alt={item.label} className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-600" loading="lazy" />
            </div>
            <p className="text-[9px] text-muted-foreground leading-tight mt-1 line-clamp-1 px-0.5 font-medium">{item.label}</p>
          </div>
        ))}
        {Array.from({ length: Math.max(0, 4 - items.length) }).map((_, i) => (
          <div key={`empty-${i}`}><div className="aspect-square rounded-xl bg-muted/8 border border-border/10" /></div>
        ))}
      </div>

      <button onClick={onAction} className="px-3 py-3 text-[11px] font-bold text-primary text-start hover:text-primary/80 transition-colors flex items-center gap-1 group/btn border-t border-border/20 mt-2">
        {actionLabel}
        <ChevronRight size={12} className="group-hover/btn:translate-x-0.5 transition-transform" />
      </button>
    </motion.div>
  );
};

/* ─── Section Header ─── */
const SectionHeader = forwardRef<HTMLDivElement, { title: string; icon?: React.ReactNode; onSeeAll?: () => void; badge?: React.ReactNode }>(
  ({ title, icon, onSeeAll, badge }, ref) => (
    <div ref={ref} className="flex items-center justify-between px-3 mb-3">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center">
          {icon}
        </div>
        <h2 className="text-[13px] font-extrabold tracking-tight">{title}</h2>
        {badge}
      </div>
      {onSeeAll && (
        <button onClick={onSeeAll} className="text-[11px] text-primary font-semibold flex items-center gap-0.5 hover:underline">
          Voir plus <ArrowRight size={12} />
        </button>
      )}
    </div>
  )
);
SectionHeader.displayName = 'SectionHeader';

export default HomePage;
